import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LENSES = [
  ["Your Wisdom", "Dinacharya", "How much of your wisdom is embedded in your business?"],
  ["Your Business", "Panchatanthra", "Is your business becoming valuable in the eyes of astute investors?"],
  ["Your Family", "Viveka Kartha", "How is your family preparing itself to embrace what you have built?"],
];

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email, password);
    if (!res.ok) setError(res.error);
    setBusy(false);
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.05fr_1fr]">
      <div className="relative hidden overflow-hidden bg-navy lg:block">
        <img
          src="https://images.unsplash.com/photo-1675985024604-bcbde41e8d1f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbHV4dXJ5JTIwYWJzdHJhY3QlMjB0ZXh0dXJlJTIwZ29sZCUyMG5hdnl8ZW58MHx8fHwxNzg3ODE2MjQ0fDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-45"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(160deg, hsl(233 44% 7% / 0.93), hsl(234 48% 12% / 0.82))," +
              "radial-gradient(52rem 34rem at 12% 4%, hsl(186 74% 38% / 0.26), transparent 62%)," +
              "radial-gradient(40rem 28rem at 88% 96%, hsl(288 46% 44% / 0.3), transparent 62%)",
          }}
        />
        <div className="grain-dark relative flex h-full flex-col justify-between p-16 text-white">
          <p className="text-[10px] font-semibold uppercase tracking-[0.34em] text-gold">
            Aikyam · Family Business 360°
          </p>
          <div>
            <h2 className="font-display text-6xl font-normal leading-[1.05]">
              Three perspectives.
              <br />
              <span className="text-gold">One purpose.</span>
            </h2>
            <div className="mt-14 space-y-7">
              {LENSES.map(([lens, name, question], i) => (
                <div key={name} className="flex gap-5">
                  <span
                    className={`mt-1 h-12 w-[3px] rounded-full ${
                      ["bg-teal", "bg-terracotta", "bg-plum"][i]
                    }`}
                  />
                  <div>
                    <p className="text-[9px] font-semibold uppercase tracking-[0.26em] text-white/50">{lens}</p>
                    <p className="font-display text-2xl">{name}</p>
                    <p className="mt-1.5 max-w-sm text-sm leading-relaxed text-white/65">{question}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <p className="text-[10px] uppercase tracking-[0.26em] text-white/40">
            Scalable · Saleable · Transferable
          </p>
        </div>
      </div>

      <div className="relative flex items-center justify-center overflow-hidden bg-card px-6 py-16 md:px-16">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(28rem 20rem at 108% -8%, hsl(30 93% 63% / 0.10), transparent 70%)," +
              "radial-gradient(24rem 18rem at -12% 108%, hsl(173 55% 45% / 0.09), transparent 70%)",
          }}
        />
        <form onSubmit={submit} className="relative z-10 w-full max-w-sm" data-testid="login-form">
          <img src={LOGO} alt="Aikyam" className="mb-7 h-24 w-auto sm:h-28" />
          <div className="mb-7 flex items-center gap-2" aria-hidden>
            <span className="h-[3px] w-14 rounded-full bg-gradient-to-r from-navy via-deepblue to-teal" />
            <span className="h-1.5 w-1.5 rounded-full bg-gold" />
            <span className="h-[3px] w-6 rounded-full bg-gold/45" />
            <span className="h-1 w-1 rounded-full bg-teal/60" />
          </div>
          <p className="label-caps text-deepblue">Secure sign in</p>
          <h1 className="mt-3 font-display text-4xl font-normal tracking-tight text-navy">Family Business 360°</h1>
          <p className="mt-4 text-sm text-muted-foreground">
            Access is scoped by engagement, framework, org scope and confidentiality class.
          </p>

          <div className="mt-10 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="label-caps">Work email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 border-border bg-card"
                placeholder="you@company.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="label-caps">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 border-border bg-card"
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <p data-testid="login-error" className="mt-5 rounded-md border-l-4 border-destructive bg-destructive/8 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}

          <Button
            type="submit"
            data-testid="login-submit-button"
            disabled={busy}
            className="mt-8 h-11 w-full rounded-full bg-navy text-[11px] uppercase tracking-[0.22em] text-white transition-[background-color,box-shadow] duration-300 hover:bg-gold hover:text-navy hover:shadow-goldglow"
          >
            {busy ? "Verifying…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
